<template>
  <div>
    <SnackbarComp :SnackbarComponent="SnackbarComponent" />
    <v-dialog v-model="internalDialog" :max-width="550" persistent>
      <v-card class="rounded-lg">
        <v-card-title
          class="d-flex align-center bg-primary text-white pa-2 font-weight-bold"
        >
          <v-icon class="mr-2">{{
            dialogType === "create" ? "mdi-plus" : "mdi-pencil"
          }}</v-icon>
          {{ dialogType === "create" ? "Add Curator" : "Edit Curator" }}
          <v-spacer></v-spacer>
          <v-btn variant="text" icon @click="closeDialog">
            <v-icon>mdi-close-thick</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-4">
          <v-form ref="adminForm">
            <v-row>
              <v-col cols="12" md="12" sm="12" class="py-2">
                <v-text-field
                  v-model="formData.user_name"
                  label="Name *"
                  density="compact"
                  placeholder="Enter admin name"
                  variant="outlined"
                  hide-details="auto"
                  :rules="[required]"
                  prepend-inner-icon="mdi-account"
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="12" sm="12" class="py-2">
                <v-text-field
                  v-model="formData.user_email_id"
                  label="Email *"
                  placeholder="Enter email address"
                  density="compact"
                  type="email"
                  variant="outlined"
                  hide-details="auto"
                  :rules="[required, emailRule]"
                  prepend-inner-icon="mdi-email"
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="12" sm="12" class="py-2">
                <v-select
                  v-model="formData.user_role"
                  :items="[
                    { title: 'Super Curator', value: 'SUPER_CURATOR' },
                    { title: 'Curator', value: 'CURATOR' },
                  ]"
                  label="Role *"
                  placeholder="Select user role"
                  density="compact"
                  variant="outlined"
                  hide-details="auto"
                  :rules="[required]"
                  prepend-inner-icon="mdi-shield-account"
                  @update:modelValue="handleRoleChange"
                ></v-select>
              </v-col>

              <v-row
                v-if="formData.user_role === 'CURATOR'"
                class="py-2 px-3 mb-2"
              >
                <v-col cols="12" md="6" sm="6" class="py-2">
                  <v-autocomplete
                    v-model="selectedCountry"
                    label="Country"
                    :items="['ALL', 'India', 'United States']"
                    density="compact"
                    variant="outlined"
                    hide-details="auto"
                    @update:modelValue="onCountryChange"
                  ></v-autocomplete>
                </v-col>

                <v-col
                  cols="12"
                  md="6"
                  sm="6"
                  v-if="selectedCountry !== 'ALL'"
                  class="py-2"
                >
                  <v-autocomplete
                    v-model="selectedState"
                    label="State"
                    :items="['ALL', ...availableStates]"
                    density="compact"
                    variant="outlined"
                    hide-details="auto"
                    :disabled="selectedPoll?.type === 'edit'"
                    @update:modelValue="onStateChange"
                  ></v-autocomplete>
                </v-col>

                <v-col
                  cols="12"
                  md="6"
                  sm="6"
                  v-if="selectedCountry !== 'ALL' && selectedState !== 'ALL'"
                  class="py-2"
                >
                  <v-autocomplete
                    v-model="selectedDistrict"
                    label="District"
                    :items="['ALL', ...availableDistricts]"
                    density="compact"
                    variant="outlined"
                    hide-details="auto"
                    :disabled="selectedPoll?.type === 'edit'"
                    @update:modelValue="onDistrictChange"
                  ></v-autocomplete>
                </v-col>

                <v-col
                  cols="12"
                  md="6"
                  sm="6"
                  v-if="
                    selectedCountry !== 'ALL' &&
                    selectedState !== 'ALL' &&
                    selectedDistrict !== 'ALL'
                  "
                  class="py-2"
                >
                  <v-autocomplete
                    v-model="selectedPincode"
                    label="Pincode"
                    :items="['ALL', ...availablePincodes]"
                    density="compact"
                    variant="outlined"
                    hide-details="auto"
                    :disabled="selectedPoll?.type === 'edit'"
                  ></v-autocomplete>
                </v-col>
              </v-row>
            </v-row>
          </v-form>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="pa-3 bg-grey-lighten-5">
          <v-spacer></v-spacer>
          <v-btn variant="plain" @click="closeDialog">Cancel</v-btn>
          <v-btn
            :loading="loading"
            @click="createEditAdmin"
            color="primary"
            variant="flat"
            class="bg-gradient-primary px-4"
          >
            {{ dialogType === "create" ? "Create Curator" : "Save Changes" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { createAdminUser, updateAdminUser } from "@/graphql/mutations.js";
import { generateClient } from "aws-amplify/api";
import SnackbarComp from "@/components/Extras/SnackbarComp.vue";
const client = generateClient();

export default {
  name: "AdminDialog", // Fixed typo from user_name to name
  props: {
    dialog: {
      type: Boolean,
      default: false,
    },
    dialogType: {
      type: String,
      default: "create",
    },
    selectedAdmin: {
      type: Object,
      default: null,
    },
    internalPincodes: {
      type: Array,
      default: () => [],
    },
  },
  components: {
    SnackbarComp,
  },
  emits: ["update:dialog", "clicked"],
  model: {
    prop: "dialog",
    event: "update:dialog",
  },
  data() {
    return {
      internalDialog: false,
      loading: false,

      selectedCountry: "ALL",
      selectedState: "ALL",
      selectedDistrict: "ALL",
      selectedPincode: "ALL",

      modules: [
        { key: "manage_polls", label: "Manage Polls" },
        { key: "poll_requests", label: "Poll Requests" },
        { key: "curator_requests", label: "Curator Requests" },
        { key: "poll_reports", label: "Poll Reports" },
        { key: "manage_admins", label: "Manage Admins" },
      ],

      formData: {
        user_name: "",
        user_email_id: "",
        user_role: "CURATOR",
        user_permissions: {},
      },

      SnackbarComponent: {
        SnackbarVmodel: false,
        SnackbarText: "",
        SnackbarColor: "success",
      },
      required: (value) => !!value || "Required",
      emailRule: (value) =>
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) || "Invalid email format",
    };
  },
  computed: {
    availableStates() {
      if (!this.internalPincodes || this.selectedCountry === "ALL") return [];

      const states = new Set(
        this.internalPincodes
          // Filter by the selected country first!
          .filter((item) => item.Country === this.selectedCountry)
          .map((item) => item.StateName),
      );
      return Array.from(states).filter(Boolean).sort();
    },
    availableDistricts() {
      if (!this.internalPincodes || this.selectedState === "ALL") return [];
      const districts = new Set(
        this.internalPincodes
          .filter((item) => item.StateName === this.selectedState)
          .map((item) => item.District),
      );
      return Array.from(districts).filter(Boolean).sort();
    },
    availablePincodes() {
      if (!this.internalPincodes || this.selectedDistrict === "ALL") return [];
      const pincodes = new Set(
        this.internalPincodes
          .filter(
            (item) =>
              item.District === this.selectedDistrict &&
              item.StateName === this.selectedState,
          )
          .map((item) => item.Pincode),
      );
      return Array.from(pincodes).filter(Boolean).sort();
    },
  },
  watch: {
    dialog(newVal) {
      this.internalDialog = newVal;

      if (newVal && this.dialogType === "edit" && this.selectedAdmin) {
        // Hydrate existing data
        this.formData = { ...this.selectedAdmin };

        // If API doesn't return a permissions object, initialize based on role
        if (!this.formData.user_permissions) {
          this.applyRolePermissions(this.formData.user_role);
        }

        // --- AUTO-POPULATE TARGET DROPDOWNS ---
        const tType = this.selectedAdmin.user_target_type || "GLOBAL";
        const tVal = this.selectedAdmin.user_target_value || "GLOBAL";

        this.selectedCountry = "ALL";
        this.selectedState = "ALL";
        this.selectedDistrict = "ALL";
        this.selectedPincode = "ALL";

        if (tType === "COUNTRY") {
          this.selectedCountry = tVal;
        } else if (tType === "STATE") {
          this.selectedCountry = "INDIA";
          this.selectedState = tVal;
        } else if (tType === "DISTRICT") {
          this.selectedCountry = "INDIA";
          const loc = this.internalPincodes.find((p) => p.District === tVal);
          if (loc) this.selectedState = loc.StateName;
          this.selectedDistrict = tVal;
        } else if (tType === "PINCODE") {
          this.selectedCountry = "INDIA";
          const loc = this.internalPincodes.find((p) => p.Pincode === tVal);
          if (loc) {
            this.selectedState = loc.StateName;
            this.selectedDistrict = loc.District;
          }
          this.selectedPincode = tVal;
        }
        // ----------------------------------------
      } else if (newVal && this.dialogType === "create") {
        this.resetForm();
      } else if (!newVal) {
        this.resetForm();
      }
    },
    internalDialog(newVal) {
      this.$emit("update:dialog", newVal);
    },
  },
  methods: {
    onCountryChange() {
      if (this.selectedPoll?.type === "edit") return;
      if (this.selectedState !== "ALL") this.selectedState = "ALL";
      if (this.selectedDistrict !== "ALL") this.selectedDistrict = "ALL";
      if (this.selectedPincode !== "ALL") this.selectedPincode = "ALL";
    },
    onStateChange() {
      if (this.selectedDistrict !== "ALL") this.selectedDistrict = "ALL";
      if (this.selectedPincode !== "ALL") this.selectedPincode = "ALL";
    },
    onDistrictChange() {
      if (this.selectedPincode !== "ALL") this.selectedPincode = "ALL";
    },
    resolveTargetPayload() {
      let type = "GLOBAL";
      let value = "GLOBAL";

      if (this.selectedCountry !== "ALL") {
        type = "COUNTRY";
        value = this.selectedCountry;
        if (this.selectedState !== "ALL") {
          type = "STATE";
          value = this.selectedState;
          if (this.selectedDistrict !== "ALL") {
            type = "DISTRICT";
            value = this.selectedDistrict;
            if (this.selectedPincode !== "ALL") {
              type = "PINCODE";
              value = this.selectedPincode;
            }
          }
        }
      }
      return { target_type: type, target_value: value };
    },
    handleRoleChange(newRole) {
      // Whenever the user changes the role dropdown, recalculate defaults
      this.applyRolePermissions(newRole);
    },
    applyRolePermissions(role) {
      const perms = {};
      this.modules.forEach((mod) => {
        perms[mod.key] = {
          create: true,
          list: true,
          update: true,
          // SUPER_CURATOR gets everything. CURATOR gets everything EXCEPT delete.
          delete: role === "SUPER_CURATOR",
        };
      });
      this.formData.user_permissions = perms;
    },
    async createEditAdmin() {
      const { valid } = await this.$refs.adminForm.validate();
      if (!valid) {
        this.SnackbarComponent = {
          SnackbarVmodel: true,
          SnackbarText: "Please fill in all required fields.",
          SnackbarColor: "error",
        };
        return;
      }
      const targetData = this.resolveTargetPayload();
      try {
        this.loading = true;
        let result = await client.graphql({
          query:
            this.dialogType === "create" ? createAdminUser : updateAdminUser,
          variables: {
            input: {
              current_user_id:
                this.dialogType === "edit"
                  ? this.$store.getters.get_current_user_details.user_id
                  : undefined,
              user_id:
                this.dialogType === "edit"
                  ? this.selectedAdmin.user_id
                  : this.$store.getters.get_current_user_details.user_id,
              user_name: this.formData.user_name,
              user_email_id:
                this.dialogType === "edit"
                  ? undefined
                  : this.formData.user_email_id,
              user_role: this.formData.user_role,
              user_target_type:
                this.formData.user_role == "CURATOR"
                  ? targetData.target_type
                  : undefined,
              user_target_value:
                this.formData.user_role == "CURATOR"
                  ? targetData.target_value
                  : undefined,
            },
          },
        });

        let mutationType =
          this.dialogType === "create" ? "createAdminUser" : "updateAdminUser";

        var ResultObj = JSON.parse(result.data[mutationType]);
        if (ResultObj.status == 200 || ResultObj.status === "SUCCESS") {
          this.SnackbarComponent = {
            SnackbarVmodel: true,
            SnackbarText: ResultObj.status_message,
            SnackbarColor: "success",
          };
          this.$emit("clicked", 2);
          this.closeDialog();
        }
        this.loading = false;
      } catch (error) {
        this.loading = false;
        console.log("Error", error);
        this.SnackbarComponent = {
          SnackbarVmodel: true,
          SnackbarText:
            error.errors?.[0]?.message ||
            "An error occurred. Please try again.",
          SnackbarColor: "error",
        };
        this.closeDialog();
      }
    },
    closeDialog() {
      this.internalDialog = false;
      this.resetForm();
    },
    resetForm() {
      this.formData = {
        user_name: "",
        user_email_id: "",
        user_role: "CURATOR",
        user_permissions: {},
      };

      // Reset geographic targeting
      this.selectedCountry = "ALL";
      this.selectedState = "ALL";
      this.selectedDistrict = "ALL";
      this.selectedPincode = "ALL";

      // Default initialization
      this.applyRolePermissions("CURATOR");

      if (this.$refs.adminForm) {
        this.$refs.adminForm.resetValidation();
      }
    },
  },
  mounted() {
    // Generate empty/default structure on mount just in case
    this.applyRolePermissions(this.formData.user_role);
  },
};
</script>

<style scoped>
.bg-gradient-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
}

/* Custom Table Styles for Maximum Compactness */
.permissions-table {
  border-collapse: collapse;
}

.permissions-table th {
  padding: 8px 12px;
  border-bottom: 1px solid #e0e0e0;
}

.permissions-table td {
  padding: 2px 12px; /* Tight padding for v-checkbox-btn */
}

.table-row-border {
  border-bottom: 1px solid #eeeeee;
}

.table-row-border:last-child {
  border-bottom: none;
}
</style>
